import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Film, ImageOff, Link2Off, Loader2, Pencil, RefreshCw, Search, Upload } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import KbUnownedFindings from "@/components/KbUnownedFindings";
import KbEmbedRepairCard from "@/components/KbEmbedRepairCard";

const TABS = [
  { key: "image", label: "Images", icon: ImageOff },
  { key: "embed", label: "Videos", icon: Film },
  { key: "link", label: "Links", icon: Link2Off },
];
const STATUS_LABEL = {
  broken: { text: "Broken", cls: "bg-rose-100 text-rose-700" },
  blocked: { text: "Can't verify", cls: "bg-amber-100 text-amber-700" },
  warning: { text: "Points at a draft", cls: "bg-sky-100 text-sky-700" },
};

/** Everything in the KB that no longer resolves — a dead vendor screenshot, a
 *  retired support page, one of our own images missing from storage. One card
 *  per article because a single broken host is one job, not forty. */
export default function KBImageHealth() {
  const navigate = useNavigate();
  const [kind, setKind] = usePersistedState("kb-image-health.kind", "image");
  const [status, setStatus] = usePersistedState("kb-image-health.status", "");
  const [host, setHost] = useState("");
  const [q, setQ] = usePersistedState("kb-image-health.q", "");
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [rechecking, setRechecking] = useState("");

  const recheck = async (slug) => {
    setRechecking(slug);
    try {
      const { data: r } = await api.post("/kb/link-health/recheck", { slug });
      if (!r.total) toast.success("All clear — nothing in that article is broken any more");
      else toast.info(`${r.total} still need attention — ${r.images} image${r.images === 1 ? "" : "s"}, ${r.embeds} video${r.embeds === 1 ? "" : "s"}, ${r.links} link${r.links === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't re-check that article");
    } finally { setRechecking(""); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind });
      if (status) params.set("status", status);
      if (host) params.set("host", host);
      if (q.trim()) params.set("q", q.trim());
      const [list, sum] = await Promise.all([
        api.get(`/kb/link-health?${params.toString()}`),
        api.get("/kb/link-health/summary"),
      ]);
      setData(list.data);
      setSummary(sum.data);
    } catch (e) {
      // Non-editors have no business here — send them back rather than leaving
      // them on an empty editor shell with a permission error.
      if (e?.response?.status === 403) { navigate("/kb", { replace: true }); return; }
      toast.error(e?.response?.data?.detail || "Couldn't load the health report");
    } finally { setLoading(false); }
  }, [kind, status, host, q, navigate]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // The sweep runs in the background; poll until it reports it has finished.
  useEffect(() => {
    if (!checking) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data: s } = await api.get("/kb/link-health/summary");
        if (!s.running) {
          setChecking(false);
          toast.success("Check finished");
          load();
        } else setSummary(s);
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [checking, load]);

  const runCheck = async () => {
    try {
      await api.post("/kb/link-health/check");
      setChecking(true);
      toast.info("Checking every image and link — this takes a few minutes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the check");
    }
  };

  const counts = data?.counts || {};

  return (
    <div className="kb-surface" data-testid="kb-image-health-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to="/kb" className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary"
          data-testid="kb-image-health-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Knowledge Base
        </Link>
        <Button size="sm" variant="outline" onClick={runCheck} disabled={checking || summary?.running}
          data-testid="kb-image-health-check">
          {checking || summary?.running
            ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Checking…</>
            : <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Check now</>}
        </Button>
      </div>

      <h1 className="font-heading text-3xl font-bold text-slate-900 dark:text-slate-100">Image &amp; link health</h1>
      <p className="mt-1.5 max-w-3xl text-sm text-slate-500 dark:text-slate-400" data-testid="kb-image-health-subtitle">
        {summary?.checked ? (
          <>
            {(summary.images_ok || 0).toLocaleString()} of {(summary.images_total || 0).toLocaleString()} images,{" "}
            {(summary.embeds_ok || 0).toLocaleString()} of {(summary.embeds_total || 0).toLocaleString()} videos and{" "}
            {(summary.links_ok || 0).toLocaleString()} of {(summary.links_total || 0).toLocaleString()} links resolve
            cleanly across {(summary.articles_checked || 0).toLocaleString()} articles. Checked weekly — last run{" "}
            {(summary.finished || "").slice(0, 16).replace("T", " ")}. Our own images are spot-checked{" "}
            {(summary.storage_checked || 0)} at a time out of {(summary.storage_total || 0).toLocaleString()}, so every
            file gets proven over a few weeks rather than moved every Sunday.
          </>
        ) : "No check has run yet. Press “Check now” to sweep every image and link in the KB."}
      </p>

      <div className="mt-4 space-y-3">
        <KbUnownedFindings onAssigned={load} />
        <KbEmbedRepairCard onRepaired={load} />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="kb-image-health-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => { setKind(t.key); setHost(""); }}
            data-testid={`kb-image-health-tab-${t.key}`}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              kind === t.key ? "bg-slate-900 text-white" : "border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-primary/40"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
            <span className={kind === t.key ? "text-white/60" : "text-slate-400 dark:text-slate-500"}>
              {kind === t.key ? data?.total_items ?? "" : ""}
            </span>
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        {["", "broken", "blocked", "warning"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)}
            data-testid={`kb-image-health-status-${s || "all"}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              status === s ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                : "border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-primary/40"}`}>
            {s === "" ? "Everything" : STATUS_LABEL[s].text}
            {s && counts[s] != null && <span className="ml-1 text-slate-400 dark:text-slate-500">{counts[s]}</span>}
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search article or URL"
            data-testid="kb-image-health-search" className="h-9 pl-9 text-sm" />
        </div>
      </div>

      {(data?.hosts || []).length > 1 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="kb-image-health-hosts">
          <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">By host</span>
          <button onClick={() => setHost("")} data-testid="kb-image-health-host-all"
            className={`rounded-md px-2 py-1 text-[11px] font-medium ${host === "" ? "bg-slate-900 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}>
            All
          </button>
          {data.hosts.map((h) => (
            <button key={h.host} onClick={() => setHost(h.host)} data-testid={`kb-image-health-host-${h.host}`}
              className={`rounded-md px-2 py-1 text-[11px] font-medium ${host === h.host ? "bg-slate-900 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}>
              {h.host} <span className={host === h.host ? "text-white/60" : "text-slate-400 dark:text-slate-500"}>{h.count}</span>
            </button>
          ))}
        </div>
      )}

      {loading && !data && (
        <div className="mt-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
      )}

      {data && data.articles.length === 0 && (
        <Card className="mt-6 px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="kb-image-health-empty">
          {summary?.checked
            ? "Nothing to fix here — every image and link in this filter resolves."
            : "Run a check to populate this list."}
        </Card>
      )}

      <div className="mt-5 space-y-3">
        {(data?.articles || []).map((g) => (
          <Card key={g.slug} className="px-5 py-4" data-testid={`kb-image-health-article-${g.slug}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate font-heading text-base font-semibold text-slate-800 dark:text-slate-100">{g.article}</h3>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {g.category}
                  {g.draft && <span className="ml-2 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400">DRAFT</span>}
                  <span className="ml-2">
                    {g.items.length} {kind === "image" ? "image" : kind === "embed" ? "video" : "link"}{g.items.length === 1 ? "" : "s"} need attention
                  </span>
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button type="button" onClick={() => recheck(g.slug)} disabled={rechecking === g.slug}
                  data-testid={`kb-image-health-recheck-${g.slug}`}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/40 hover:text-primary disabled:opacity-60">
                  {rechecking === g.slug
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Re-checking…</>
                    : <><RefreshCw className="h-3.5 w-3.5" /> Re-check</>}
                </button>
                <Link to={`/kb/a/${g.slug}`} data-testid={`kb-image-health-open-${g.slug}`}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/40 hover:text-primary">
                  <ExternalLink className="h-3.5 w-3.5" /> Open
                </Link>
                <Link to={`/kb/a/${g.slug}/edit`} data-testid={`kb-image-health-edit-${g.slug}`}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/40 hover:text-primary">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Link>
              </div>
            </div>
            <ul className="mt-3 space-y-2">
              {g.items.map((it, i) => (
                <li key={`${it.url}-${i}`} className="rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/70 px-3 py-2"
                  data-testid={`kb-image-health-item-${g.slug}-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      (STATUS_LABEL[it.status] || STATUS_LABEL.broken).cls}`}>
                      {(STATUS_LABEL[it.status] || STATUS_LABEL.broken).text}
                    </span>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{it.host}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{it.reason}</span>
                  </div>
                  {it.label && kind === "link" && (
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">Link text: “{it.label}”</p>
                  )}
                  {it.alt && (
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300" data-testid={`kb-image-health-alt-${g.slug}-${i}`}>
                      Caption / alt text: “{it.alt}”
                    </p>
                  )}
                  {it.context && (
                    <p className="mt-1 text-xs italic text-slate-500 dark:text-slate-400" data-testid={`kb-image-health-context-${g.slug}-${i}`}>
                      Sits after: …{it.context}
                    </p>
                  )}
                  <p className="mt-1 line-clamp-2 break-all font-mono text-[11px] text-slate-400 dark:text-slate-500">{it.url}</p>
                  {kind === "image" && (
                    <Link to={`/kb/a/${g.slug}/edit?fix=${encodeURIComponent(it.url)}`}
                      data-testid={`kb-image-health-replace-${g.slug}-${i}`}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 no-underline transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                      <Upload className="h-3 w-3" /> Replace this image
                    </Link>
                  )}
                  {kind === "embed" && (
                    <Link to={`/kb/a/${g.slug}/edit?fixembed=${encodeURIComponent(it.url)}`}
                      data-testid={`kb-image-health-replace-embed-${g.slug}-${i}`}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 no-underline transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                      <Film className="h-3 w-3" /> Replace this video
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </div>
  );
}
